import React from "react";
import { formatNumber } from "../shared/utils/helpers";
import { BetSlipMode } from "../types/enums";
import { useI18n } from "../providers/I18nProvider";

interface SummaryRowsProps {
  mode: BetSlipMode;
  showVat: boolean;
  exciseTaxRate: number;
  exciseTaxValue: number;
  netStake: number;
  individualStakes: { [key: string]: number };
  totalOdds: number;
  activeWinBoost: MappedBonus | null | undefined;
  isWinBoostEligible: boolean;
  winBoostAmount: number;
  isFreeBetActive: boolean;
  taxPercentage: number;
  totalTaxValue: number;
  maxPayout: number;
  currency: string;
}

export const SummaryRows: React.FC<SummaryRowsProps> = ({
  mode,
  showVat,
  exciseTaxRate,
  exciseTaxValue,
  netStake,
  individualStakes,
  totalOdds,
  activeWinBoost,
  isWinBoostEligible,
  winBoostAmount,
  isFreeBetActive,
  taxPercentage,
  totalTaxValue,
  maxPayout,
  currency,
}) => {
  const { t, tHtml } = useI18n();

  const totalSingleStake = (Object as any)
    .values(individualStakes)
    .reduce((acc: number, curr: number) => acc + curr, 0);

  return (
    <div className="flex flex-col gap-y-2 text-sm px-4 pt-4 border-t border-solid border-betslip-summary-border">
      {showVat && (
        <div
          className="flex justify-between"
          dangerouslySetInnerHTML={tHtml("BetSlipExciseTax", {
            tax: exciseTaxRate,
            taxValue: formatNumber(exciseTaxValue),
            currency,
          })}
        />
      )}

      {showVat && (
        <div
          className="flex justify-between"
          dangerouslySetInnerHTML={tHtml(
            mode === BetSlipMode.Single ? "BetSlipTotalNetStake" : "BetSlipNetStake",
            { receiptNetStake: formatNumber(netStake), currency },
          )}
        />
      )}

      {mode === BetSlipMode.Single && !showVat && (
        <div className="flex justify-between">
          <span>{t("BetSlipTotalStake", { "receiptStake:span": "" }).trim()}</span>
          <span>{formatNumber(totalSingleStake)} {currency}</span>
        </div>
      )}

      {mode === BetSlipMode.Combo && (
        <div className="flex justify-between">
          <span>{t("BetSlipTotalOdds", { "totalOdds:span": "" }).trim()}</span>
          <span>{formatNumber(totalOdds)}</span>
        </div>
      )}

      {activeWinBoost && isWinBoostEligible && !isFreeBetActive && (
        <div
          className="flex justify-between"
          dangerouslySetInnerHTML={tHtml("BetSlipBonusBoost", {
            bonusPercent: activeWinBoost.percentage,
            bonusValue: formatNumber(winBoostAmount),
            currency,
          })}
        />
      )}

      <div
        className="flex justify-between"
        dangerouslySetInnerHTML={tHtml(
          taxPercentage > 0 ? "TaxPercentage" : "NoTaxPercentage",
          {
            tax: (taxPercentage * 100).toFixed(0),
            taxValue: `${totalTaxValue < 0 ? "-" : ""}${formatNumber(Math.abs(totalTaxValue))}`,
            currency,
          },
        )}
      />

      <div
        className="flex justify-between font-semibold [&_.maxPayout]:flex [&_.maxPayout]:w-full [&_.maxPayout]:justify-between"
        dangerouslySetInnerHTML={tHtml("BetSlipMaxPayOut", {
          maxPayOut: formatNumber(maxPayout),
          currency,
        })}
      />
    </div>
  );
};
